// Client-side sort for album photo grids. The server can only order by
// createdAt (GSI1SK), so anything beyond upload date — rating today, EXIF
// captureDate tomorrow — has to be re-sorted in-memory after decrypt.
//
// Photos without a rating always sink to the bottom regardless of dir, so
// the unrated tail doesn't confuse the "least rated" view.

export type SortKey = 'uploadedAt' | 'rating';
export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: SortKey;
  dir: SortDir;
}

export const DEFAULT_SORT: SortState = { key: 'uploadedAt', dir: 'desc' };

interface SortableRow {
  photo: { createdAt: string };
  rating: number | null;
}

export function sortPhotoRows<T extends SortableRow>(rows: readonly T[], sort: SortState): T[] {
  const arr = rows.slice();
  if (sort.key === 'uploadedAt') {
    arr.sort((a, b) =>
      sort.dir === 'asc'
        ? a.photo.createdAt.localeCompare(b.photo.createdAt)
        : b.photo.createdAt.localeCompare(a.photo.createdAt),
    );
    return arr;
  }
  arr.sort((a, b) => {
    const ar = a.rating;
    const br = b.rating;
    if (ar === null && br === null) {
      return b.photo.createdAt.localeCompare(a.photo.createdAt);
    }
    if (ar === null) return 1;
    if (br === null) return -1;
    if (ar !== br) return sort.dir === 'desc' ? br - ar : ar - br;
    return b.photo.createdAt.localeCompare(a.photo.createdAt);
  });
  return arr;
}

const STORAGE_KEY = 'pv-photo-sort';

export function loadSortPreference(): SortState {
  if (typeof window === 'undefined') return DEFAULT_SORT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SORT;
    const parsed = JSON.parse(raw) as Partial<SortState>;
    const key: SortKey = parsed.key === 'rating' ? 'rating' : 'uploadedAt';
    const dir: SortDir = parsed.dir === 'asc' ? 'asc' : 'desc';
    return { key, dir };
  } catch {
    return DEFAULT_SORT;
  }
}

export function saveSortPreference(sort: SortState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sort));
  } catch {
    // localStorage full or denied — sort is just session-local then.
  }
}
